import { useMemo } from 'react'
import type { Attendee } from '../types'
import type { TimeRange } from '../utils/SchedulingUtils'
import type { BusyTimeData } from '../utils/conflictDetection'
import {
  hasBusyTimes as checkHasBusyTimes,
  hasConflict,
  hasConflictWithMultipleRanges,
} from '../utils/conflictDetection'

export type { Attendee }

/**
 * Attendee with computed status flags
 */
export interface AttendeeWithStatus extends Attendee {
  hasBusyTimes: boolean
  noData: boolean
  unknown: boolean
  hasConflict: boolean
}

/**
 * Custom hook to compute attendee status including conflict detection
 *
 * Handles both owner and regular attendees:
 * - Owner: Uses ownerHasConflict prop (based on loaded calendar events)
 * - Attendees: Uses busyData from free/busy API
 *
 * @param attendees - List of attendees with email, name, role
 * @param busyTimes - Busy time data keyed by email
 * @param selectedTimeRange - Single selected time range
 * @param selectedTimeRangeList - Multiple selected time ranges (for multi-mode)
 * @param ownerHasConflict - Owner's conflict status from parent
 * @returns Attendees with computed status flags
 */
export const useAttendeesWithStatus = (
  attendees: Attendee[],
  busyTimes: Record<string, BusyTimeData | null | undefined>,
  selectedTimeRange: (TimeRange & { allday?: boolean }) | null | undefined,
  selectedTimeRangeList: (TimeRange & { allday?: boolean })[] | null | undefined,
  ownerHasConflict: boolean,
): AttendeeWithStatus[] => {
  return useMemo(() => {
    return attendees.map((attendee) => {
      const email = attendee.email
      const isOwner = attendee.role === 'ORGANIZER'

      // For owner, use ownerHasConflict from parent (based on loaded events)
      // For attendees, use busyData conflict detection
      if (isOwner) {
        return {
          ...attendee,
          hasBusyTimes: true, // Owner always has potential busy times (loaded events)
          noData: false,
          unknown: false,
          hasConflict: ownerHasConflict,
        }
      }

      // Regular attendee - use busyData
      const busyData = busyTimes[email]

      // Use multi-range conflict detection if selections array is provided and not empty
      const hasConflictResult =
        selectedTimeRangeList && selectedTimeRangeList.length > 0
          ? hasConflictWithMultipleRanges(busyData, selectedTimeRangeList)
          : selectedTimeRange
            ? hasConflict(busyData, selectedTimeRange, selectedTimeRange.allday)
            : false

      return {
        ...attendee,
        hasBusyTimes: checkHasBusyTimes(busyData),
        noData: busyData?.noData || false,
        unknown: busyData?.unknown || false,
        hasConflict: hasConflictResult,
      }
    })
  }, [attendees, busyTimes, selectedTimeRange, selectedTimeRangeList, ownerHasConflict])
}
